import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import {
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";
import CustomerServices from "../../../../../services/OrderingServices/CustomerServices";
import toast from "react-hot-toast";
import Select from "react-select";
import CustomerTypeService from "../../../../../services/OrderingServices/CustomerTypeService";

// Validation Schema
const validationSchema = Yup.object({
  // username: Yup.string().required("Username is required"),
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Enter a valid email address"
    )
    .required("Email is required"),
  address: Yup.string().required("Address is required"),
  phoneNo: Yup.string()
    .required("Phone number is required")
    .matches(/^[0-9]+$/, "Phone number must be digits only")
    .min(10, "Phone number must be at least 10 digits")
    .max(10, "Phone number must be exactly 10 digits"),
  customerTypeId: Yup.number().nullable().required("Customer type is required"),
});
const CustomerForm = ({
  isOpen,
  toggle,
  editingPackage,
  // onSave,
  fetchCustomers,
}) => {
  const [customerTypeData, setCustomerTypeData] = useState([]);
  const fetchCustomerType = async () => {
    try {
      const response = await CustomerTypeService.getList();
      setCustomerTypeData(response);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchCustomerType();
  }, []);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: editingPackage?.email || "",
      firstName: editingPackage?.firstName || "",
      lastName: editingPackage?.lastName || "",
      phoneNo: editingPackage?.phoneNo || "",
      address: editingPackage?.address || "",
      customerTypeId: editingPackage?.customerTypeId || null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await CustomerServices.create(values);
        // onSave(response);
        toggle();
        fetchCustomers();
        resetForm();
        toast.success("New customer added successfully.");
      } catch (error) {
        toast.error(error?.response.message);
        console.log(error);
      }
    },
  });

  const handleCustomerTypeChange = (selectedOption) => {
    formik.setFieldValue(
      "customerTypeId",
      selectedOption ? selectedOption.value : null
    );
  };

  const customerTypeOptions = customerTypeData.map((type) => ({
    value: type.id,
    label: type.name,
  }));
  const selectedCustomerType = customerTypeOptions.find(
    (option) => option.value === formik.values.customerTypeId
  );
  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>Add Customer</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <Col lg={12} className="mb-3">
              <div className="form-floating">
                <Input
                  name="firstName"
                  className="form-control"
                  placeholder="Enter first name"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName}
                  invalid={
                    formik.touched.firstName && !!formik.errors.firstName
                  }
                />
                <Label>First Name</Label>
                {formik.touched.firstName && formik.errors.firstName && (
                  <FormFeedback>{formik.errors.firstName}</FormFeedback>
                )}
              </div>
            </Col>

            <Col lg={12} className="mb-3">
              <div className="form-floating">
                <Input
                  name="lastName"
                  className="form-control"
                  placeholder="Enter last name"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.lastName}
                  invalid={formik.touched.lastName && !!formik.errors.lastName}
                />
                <Label>Last Name</Label>
                {formik.touched.lastName && formik.errors.lastName && (
                  <FormFeedback>{formik.errors.lastName}</FormFeedback>
                )}
              </div>
            </Col>

            <Col lg={12} className="mb-3">
              <div className="form-floating">
                <Input
                  name="email"
                  className="form-control"
                  placeholder="Enter email"
                  type="email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  invalid={formik.touched.email && !!formik.errors.email}
                />
                <Label>Email address</Label>
                {formik.touched.email && formik.errors.email && (
                  <FormFeedback>{formik.errors.email}</FormFeedback>
                )}
              </div>
            </Col>
            <Col lg={12} className="mb-3">
              <div className="form-floating">
                <Input
                  name="address"
                  className="form-control"
                  placeholder="Enter address"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.address}
                  invalid={formik.touched.address && !!formik.errors.address}
                />
                <Label>Address</Label>
                {formik.touched.address && formik.errors.address && (
                  <FormFeedback>{formik.errors.address}</FormFeedback>
                )}
              </div>
            </Col>
            <Col lg={12} className="mb-3">
              <div className="form-floating">
                <Input
                  name="phoneNo"
                  className="form-control"
                  placeholder="Phone Number"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.phoneNo}
                  invalid={formik.touched.phoneNo && !!formik.errors.phoneNo}
                />
                <Label>Phone Number</Label>
                {formik.touched.phoneNo && formik.errors.phoneNo && (
                  <FormFeedback>{formik.errors.phoneNo}</FormFeedback>
                )}
              </div>
            </Col>
            <Col md={12}>
              <div className="mb-3">
                <Select
                  id="customerTypeId"
                  name="customerTypeId"
                  options={customerTypeOptions}
                  value={selectedCustomerType || null}
                  onChange={handleCustomerTypeChange}
                  placeholder="Select Customer Type"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (baseStyles) => ({
                      ...baseStyles,
                      padding: "0.3rem",
                    }),
                  }}
                />
                {formik.touched.customerTypeId &&
                  formik.errors.customerTypeId && (
                    <div className="text-danger">
                      {formik.errors.customerTypeId}
                    </div>
                  )}
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-soft-danger" onClick={toggle}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            type="submit"
            onClick={formik.handleSubmit}
          >
            Save
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CustomerForm;
